import React, { useState } from 'react';
import { ArrowPathRoundedSquareIcon } from '@heroicons/react/24/outline';
import inventoryApi from '../../../services/inventoryApi';
import { downloadXlsxFromAoa } from '../../../utils/excelJsSheetHelpers';
import {
  ReportShell, Field, DateInput, WarehouseSelect, ProductFilters, EMPTY_PRODUCT, productParams, productFilterLine,
  ExportButtons, printReport, runReport, qty, money, today, fmtDate, esc,
} from './shared/ReportKit';

// Re-Order: items at or below their re-order level and the quantity to order
// (maximum stock − on hand, or the re-order quantity when no maximum is set)
const ReorderReport = () => {
  const [asOfDate, setAsOfDate] = useState(today());
  const [warehouse, setWarehouse] = useState({ id: '', label: '' });
  const [product, setProduct] = useState(EMPTY_PRODUCT);
  const [showAll, setShowAll] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  const generate = () => runReport(() => inventoryApi.getReorderReport({
    asOfDate, ...(warehouse.id ? { warehouseId: warehouse.id } : {}), ...productParams(product), ...(showAll ? { showAll: 'true' } : {}),
  }), setLoading, setData);
  const reset = () => { setAsOfDate(today()); setWarehouse({ id: '', label: '' }); setProduct(EMPTY_PRODUCT); setShowAll(false); setData(null); };
  const filtersLine = [warehouse.label && `Location: ${warehouse.label}`, productFilterLine(product), showAll && 'All items'].filter(Boolean).join(' · ');

  const exportExcel = async () => {
    const rows = [['RE-ORDER REPORT'], [`As at: ${fmtDate(data.asOfDate)}`], ...(filtersLine ? [[filtersLine]] : []), [],
      ['Item Code', 'Item Name', 'Unit', 'Quantity on Hand', 'Re-Order Level', 'Re-Order Quantity', 'Required Quantity']];
    data.rows.forEach(r => rows.push([r.itemCode, r.itemName, r.unit, r.quantityOnHand, r.reorderLevel, r.reorderQuantity, r.required]));
    await downloadXlsxFromAoa(rows, 'Re-Order', `reorder-report-${data.asOfDate}.xlsx`, [{ wch: 18 }, { wch: 40 }, { wch: 8 }, { wch: 16 }, { wch: 14 }, { wch: 16 }, { wch: 16 }]);
  };
  const print = () => printReport({
    title: 'Re-Order Report', subtitle: `As at ${fmtDate(data.asOfDate)}`, filtersLine,
    tableHtml: `<table><thead><tr><th>Item Code</th><th>Item Name</th><th class="r">Qty on Hand</th><th class="r">Re-Order Level</th><th class="r">Re-Order Qty</th><th class="r">Required Qty</th></tr></thead><tbody>${
      data.rows.map(r => `<tr><td>${esc(r.itemCode)}</td><td>${esc(r.itemName)}</td><td class="r">${qty(r.quantityOnHand)} ${esc(r.unit)}</td><td class="r">${qty(r.reorderLevel)}</td><td class="r">${qty(r.reorderQuantity)}</td><td class="r"><b>${qty(r.required)}</b></td></tr>`).join('')
    }</tbody></table>`,
    note: 'Required quantity = maximum stock − quantity on hand when the item is at or below its re-order level (the re-order quantity when no maximum stock is set).',
  });

  return (
    <ReportShell
      title="Re-Order Report" subtitle="Items at or below their re-order level and the quantity to order" icon={ArrowPathRoundedSquareIcon}
      loading={loading} onGenerate={generate} onReset={reset}
      filters={<>
        <Field label="As at Date"><DateInput value={asOfDate} onChange={setAsOfDate} /></Field>
        <Field label="Location"><WarehouseSelect value={warehouse.id} onChange={(id, label) => setWarehouse({ id, label: id ? label : '' })} /></Field>
        <ProductFilters value={product} onChange={setProduct} />
        <label className="flex items-center gap-2 h-[38px] text-sm text-gray-600 cursor-pointer select-none">
          <input type="checkbox" checked={showAll} onChange={(e) => setShowAll(e.target.checked)} className="w-4 h-4 rounded border-gray-300 text-indigo-600" />
          Show all items
        </label>
      </>}
      actions={data && <>
        <span className="text-sm text-gray-600">
          As at <b className="text-indigo-600">{fmtDate(data.asOfDate)}</b> · {data.summary.belowLevel} item{data.summary.belowLevel === 1 ? '' : 's'} at or below re-order level
          {data.summary.estimatedCost > 0 && <> · estimated cost to re-order Rs {money(data.summary.estimatedCost)}</>}
        </span>
        <ExportButtons onExcel={exportExcel} onPrint={print} />
      </>}
    >
      {data && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr className="text-xs text-gray-500">
                <th className="text-left px-4 py-2 font-medium">Item Code</th>
                <th className="text-left px-4 py-2 font-medium">Item Name</th>
                <th className="text-right px-4 py-2 font-medium">Quantity on Hand</th>
                <th className="text-right px-4 py-2 font-medium">Re-Order Level</th>
                <th className="text-right px-4 py-2 font-medium">Re-Order Quantity</th>
                <th className="text-right px-4 py-2 font-medium">Required Quantity</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {data.rows.length === 0 ? (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-400 italic">{showAll ? 'No items match the filters' : 'No items are at or below their re-order level'}</td></tr>
              ) : data.rows.map(r => (
                <tr key={r.itemId} className={`hover:bg-gray-50 ${r.belowLevel ? '' : 'text-gray-500'}`}>
                  <td className="px-4 py-2 font-mono text-xs">{r.itemCode}</td>
                  <td className="px-4 py-2">{r.itemName}<div className="text-xs text-gray-400">{[r.groupName, r.typeName].filter(Boolean).join(' › ')}</div></td>
                  <td className={`px-4 py-2 text-right font-mono ${r.belowLevel ? 'text-red-600' : ''}`}>{qty(r.quantityOnHand)} <span className="text-xs text-gray-400">{r.unit}</span></td>
                  <td className="px-4 py-2 text-right font-mono">{qty(r.reorderLevel)}</td>
                  <td className="px-4 py-2 text-right font-mono">{qty(r.reorderQuantity)}</td>
                  <td className="px-4 py-2 text-right font-mono font-semibold text-indigo-700">{r.required ? qty(r.required) : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </ReportShell>
  );
};

export default ReorderReport;
